/**
 * Podskazki.tsx — ОБЩАЯ ТРУБА ПОДСКАЗОК ПАНЕЛИ (`s332`, ПР-4 и ПР-6).
 *
 * ⛔⛔ ПОВОД — БЛОКЕР ПРИЁМКИ 18.09.2026. Владелец дословно: «д5 нет ничего не
 * всплывает нигде» (ни на значках плитки, ни на значках верхнего ряда у
 * времени), и отдельно про хвост состава: «баг нашел принаведении на +3 услуги»
 * — «всплывает чёрный обрезанный блок, накрывает текст плитки и шире её».
 *
 * ⛔ КОРЕНЬ ОДИН НА ОБЕ ЖАЛОБЫ, и он замерен на живом бою: подсказка рисовалась
 * тенью самого значка (`::after`) ВНУТРИ плитки, а плитка и её верхний ряд
 * объявлены `overflow: hidden`. Разбор с числами — шапка `podskazki.css`.
 *
 * ⛔ ЧИНИТСЯ ПРАВИЛОМ НА ОБЩЕЙ ТРУБЕ (op-rules §33.2). Слой один, живёт НАД
 * маршрутами рядом с `ToastViewport`, рисуется в `document.body`. Экрану
 * подключать нечего: любой элемент с атрибутом `data-podskazka` получает
 * подсказку сам — это и есть критерий приёмки трубы.
 *
 * ⛔ ПОЧЕМУ НЕ ВЕРНУЛИ `title`. Браузер ждёт своей секунды и рисует подсказку
 * системой — владелец просил обратное (К3, 17.09.2026): «при наведении мышки на
 * любую иконку — МОМЕНТАЛЬНО выводить подсказку». Плюс `title` не умеет ни
 * переноса строк, ни разворота у края экрана.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import './podskazki.css';

/** Поле от края окна, за которое подсказка не заходит. */
const POLE = 8;
/** Зазор между носителем и подсказкой. */
const ZAZOR = 6;

type Cel = { tekst: string; ankor: DOMRect; korob: DOMRect | null };

/*
 * ⛔⛔ ПОДСКАЗКА ВСТАЁТ У СВОЕГО ЭЛЕМЕНТА, А НЕ У ЕГО КОРОБКИ (`s333`, слово
 * владельца 19.09.2026: «выводится моментально но не там где надо, надо рядом
 * с иконкой»). Это ОТМЕНЯЕТ обход коробки, введённый `s332` (ПР-4).
 *
 * ⛔ ПОЧЕМУ ОТМЕНА, А НЕ ПОДКРУТКА. `s332` привязывала подсказку к плитке
 * целиком и ставила её ПОД плиткой. Плитки журнала стоят вплотную: низ одной —
 * верх следующей, и подсказка от значка записи 16:00-17:30 садилась на плитку
 * 17:30-19:00, а подсказка от «+ещё 2» — на плитку ниже. Владелец читал её как
 * принадлежащую соседке. Якорь у своего элемента убирает это по построению:
 * расстояние от элемента до подсказки — ОДНО ЧИСЛО (`ZAZOR`) на первой,
 * средней и последней плитке дня, потому что считается от самого элемента.
 *
 * ⛔ СВОЙ ТЕКСТ ПОДСКАЗКА НАКРЫВАТЬ МОЖЕТ, ЧУЖОЙ — НЕТ. Ровно это владелец и
 * просил: «вплотную к своему элементу… не наезжает на соседние плитки».
 */
const yakor = (n: HTMLElement): DOMRect => n.getBoundingClientRect();

/*
 * ⛔⛔ КОРОБКА БОЛЬШЕ НЕ ЯКОРЬ, А ГРАНИЦА (`s333`). `s332` ставила подсказку у
 * коробки — владелец это отверг («надо рядом с иконкой»). Но сама коробка
 * нужна для ВТОРОГО его условия: «не наезжает на соседние плитки». Плитки
 * журнала стоят вплотную — низ одной есть верх следующей, — и подсказка,
 * вылезшая за нижний край своей плитки, оказывается на чужой записи.
 *
 * Поэтому `data-podskazka-obhod` остался, но означает другое: «вот моя
 * территория». Внутри неё подсказка ищет место сама: сперва под элементом,
 * не влезла — над ним.
 */
const granica = (n: HTMLElement): DOMRect | null => {
  const k = n.closest<HTMLElement>('[data-podskazka-obhod]');

  return k === null ? null : k.getBoundingClientRect();
};

/** Половина ширины хвостика — им же он отступает от скруглённого угла. */
const HVOST = 7;

type Poza = { left: number; top: number; snizu: boolean; hvost: number; sboku: boolean };

export function SloyPodskazok() {
  const [cel, setCel] = useState<Cel | null>(null);
  const [poza, setPoza] = useState<Poza | null>(null);
  const telo = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    /**
     * ⛔⛔ ПОДСКАЗКА, ПОВТОРЯЮЩАЯ ВИДИМОЕ, НЕ ПОКАЗЫВАЕТСЯ ВОВСЕ (владелец,
     * 19.09.2026, живая приёмка, дословно: «кто просил делать всплывающие
     * дублирующие подсказки??? просилось только показывать то что скрыто под
     * +еще например и только!!»).
     *
     * ⛔ ЭТО ПРАВИЛО НА ОБЩЕЙ ТРУБЕ, А НЕ СНЯТИЕ ПОДСКАЗОК ПО МЕСТАМ
     * (op-rules §33.2). Носителей в участке записи больше двадцати, в трёх
     * плашках и на полке; вычеркни их руками — и следующая добавленная
     * подсказка снова начнёт дублировать видимое, а ловушка этого не заметит.
     * Здесь один вопрос на всю панель: «эта подсказка говорит что-то, чего на
     * экране НЕ ВИДНО?» Нет — молчим.
     *
     * Остаются ровно два случая, названные владельцем:
     *   1) ЗНАЧКИ — у них видимого текста нет вовсе, смысл иначе не прочитать
     *      («значки оставляем»); сюда же тихий значок оси брони и «1 перенос»
     *      БЕЗ подписи. С подписью «1 перенос» подсказка «1 перенос» молчит
     *      сама — она дубль, и правило это видит;
     *   2) ОБРЕЗАННЫЙ ТЕКСТ и «+ещё N» — там подсказка показывает СКРЫТОЕ.
     */
    const vidimyy = (n: HTMLElement): string =>
      (n.innerText ?? n.textContent ?? '').replace(/\s+/g, ' ').trim();

    /** Текст не помещается в свою коробку — многоточие или срез. */
    const obrezan = (n: HTMLElement): boolean =>
      n.scrollWidth > n.clientWidth + 1 || n.scrollHeight > n.clientHeight + 1;

    const nositel = (e: Event): HTMLElement | null => {
      const t = e.target;
      if (! (t instanceof Element)) return null;
      const n = t.closest<HTMLElement>('[data-podskazka]');
      if (n === null) return null;
      const tekst = (n.dataset.podskazka ?? '').trim();
      if (tekst === '') return null;

      /*
       * ⛔ ЯВНЫЙ ФЛАГ ДЛЯ ТЕХ, ГДЕ ПОДСКАЗКА ДЛИННЕЕ ВИДИМОГО, НО СМЫСЛ ТОТ ЖЕ:
       * полное ФИО под коротким именем, полное название услуги под срезанным.
       * Совпадением строк такие не поймать — они честно разные, а показывать
       * их, пока текст влезает целиком, всё равно нечего.
       */
      if (n.dataset.podskazkaObrez !== undefined && ! obrezan(n)) return null;

      /* Дубль видимого: показывать нечего, пока текст не срезан. */
      if (tekst === vidimyy(n) && ! obrezan(n)) return null;

      return n;
    };

    const pokazat = (e: Event) => {
      const n = nositel(e);
      if (n === null) return;
      setCel({ tekst: (n.dataset.podskazka ?? '').trim(), ankor: yakor(n), korob: granica(n) });
    };

    const skryt = (e: Event) => {
      /* Уходим только когда указатель покинул самого носителя, а не перешёл
         на его внутренний `path` внутри `svg`. */
      if (e.type === 'pointerout') {
        const ev = e as PointerEvent;
        const kuda = ev.relatedTarget;
        const n = nositel(e);
        if (n !== null && kuda instanceof Node && n.contains(kuda)) return;
      }
      setCel(null);
    };

    const klavisha = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCel(null);
    };

    document.addEventListener('pointerover', pokazat, true);
    document.addEventListener('pointerout', skryt, true);
    document.addEventListener('focusin', pokazat, true);
    document.addEventListener('focusout', skryt, true);
    /* ⛔ Прокрутка и нажатие уводят носитель из-под замеренной точки: подсказка
       осталась бы висеть над пустым местом. */
    document.addEventListener('scroll', skryt, true);
    document.addEventListener('pointerdown', skryt, true);
    window.addEventListener('resize', skryt);
    document.addEventListener('keydown', klavisha);

    return () => {
      document.removeEventListener('pointerover', pokazat, true);
      document.removeEventListener('pointerout', skryt, true);
      document.removeEventListener('focusin', pokazat, true);
      document.removeEventListener('focusout', skryt, true);
      document.removeEventListener('scroll', skryt, true);
      document.removeEventListener('pointerdown', skryt, true);
      window.removeEventListener('resize', skryt);
      document.removeEventListener('keydown', klavisha);
    };
  }, []);

  /*
   * ⛔ МЕСТО СЧИТАЕТСЯ ПОСЛЕ ЗАМЕРА САМОЙ ПОДСКАЗКИ, а не до: ширину и высоту
   * задаёт текст, и «на глаз» они не считаются. Первый кадр подсказка
   * невидима, во втором встаёт на место — задержки для глаза это не даёт.
   */
  useLayoutEffect(() => {
    if (cel === null || telo.current === null) {
      setPoza(null);
      return;
    }
    const t = telo.current.getBoundingClientRect();
    const a = cel.ankor;

    /*
     * ⛔⛔ ПО УМОЛЧАНИЮ — ПОД СВОИМ ЭЛЕМЕНТОМ, ВПЛОТНУЮ (владелец, 18.09.2026:
     * «помоему логичнее ее ниже открывать»; 19.09.2026: «надо рядом с
     * иконкой»). Зазор один и тот же для любого носителя — верхнего ряда
     * значков, нижнего ряда и хвоста «+ещё N», — потому что считается от
     * самого носителя, а не от его плитки.
     *
     * ⛔ ВВЕРХ — ТОЛЬКО КОГДА СНИЗУ НЕ ВЛЕЗЛО (у нижнего края окна). Прижимать
     * её к краю нельзя: она оторвалась бы от носителя и села бы на чужой.
     */
    const k = cel.korob;
    const vnizu = a.bottom + ZAZOR;
    const vverhu = a.top - t.height - ZAZOR;

    /*
     * ⛔ «НЕ НАЕЗЖАЕТ НА СОСЕДНИЕ ПЛИТКИ» ПРОВЕРЯЕТСЯ ПО СВОЕЙ КОРОБКЕ, А НЕ
     * ПО ОКНУ. Проверь только окно — и подсказка от хвоста «+ещё N», который
     * стоит у нижнего края плитки, честно влезет в экран и сядет на соседнюю
     * запись: ровно та жалоба, с которой пришёл владелец.
     */
    const vlezaetVniz = k === null ? true : vnizu + t.height <= k.bottom;
    const vlezaetVverh = k === null ? false : vverhu >= k.top;

    let snizu = vlezaetVniz || ! vlezaetVverh;
    let top = snizu ? vnizu : vverhu;

    /*
     * ⛔⛔ НЕ ВЛЕЗЛА НИ ВНИЗ, НИ ВВЕРХ — ВСТАЁТ СБОКУ ОТ СВОЕГО ЗНАЧКА
     * (владелец, 19.09.2026, недоделка `s333`: «Место подсказки: всплывает у
     * СВОЕГО элемента, на соседнюю плитку не наезжает. Проверено владельцем —
     * пока улетает вниз»).
     *
     * ⛔ ЧТО ИМЕННО «УЛЕТАЛО ВНИЗ». Здесь стоял прижим к НИЗУ своей коробки:
     * `top = k.bottom - t.height`. На часовой плитке (62 точки) подсказка от
     * значка, стоящего в ВЕРХНЕЙ полосе времени, уезжала к нижнему краю
     * плитки — до своего значка ей оставалось полплитки. Условие «не наезжает
     * на соседа» выполнялось, «рядом со своим элементом» — нет.
     *
     * ⛔ ПОЧЕМУ ВБОК, А НЕ ВНИЗ НА СОСЕДА. Владелец жаловался на соседа ПО
     * ВРЕМЕНИ — плитку следующего часа в том же столбце: накрыв её, подсказка
     * прячет запись, которую человек читает. Вбок она уходит на столбец
     * другого МАСТЕРА, и это он же назвал допустимым (`s333`, ширина 420).
     * Вертикаль остаётся у своего значка — подсказка стоит его верхом.
     */
    let sboku = false;
    if (k !== null && ! vlezaetVniz && ! vlezaetVverh) {
      sboku = true;
      snizu = true;
      top = Math.min(Math.max(POLE, a.top), window.innerHeight - POLE - t.height);
    }

    /* У нижнего края ОКНА переворачиваем всегда — иначе подсказки не видно. */
    if (snizu && top + t.height > window.innerHeight - POLE && vverhu >= POLE) {
      snizu = false;
      top = vverhu;
    }

    if (top < POLE) {
      snizu = true;
      top = Math.min(vnizu, window.innerHeight - POLE - t.height);
    }

    /* ⛔ У КРАЯ ЭКРАНА ПОДСКАЗКА ЗАВОРАЧИВАЕТСЯ ВНУТРЬ (ПР-4), а не уезжает за
       границу окна: у правого столбца журнала до края меньше её половины. */
    const seredina = a.left + a.width / 2;
    /*
     * ⛔⛔ СБОКУ — ЗА КРАЙ СВОЕЙ ПЛИТКИ, А НЕ ПРОСТО ЗА ЗНАЧОК. Первая редакция
     * отступала от самого значка (`a.right`) — и подсказка на десять строк,
     * оставшись внутри своего столбца, всё равно уехала вниз на запись
     * следующего часа. Замер это и поймал: «+ещё 9» накрыл соседа ПО ВРЕМЕНИ.
     *
     * За правым краем плитки её высота падает уже на столбец другого МАСТЕРА —
     * то, что владелец назвал допустимым, в отличие от соседа по времени.
     */
    const bokKray = k ?? a;
    let left = sboku
      ? (bokKray.right + ZAZOR + t.width <= window.innerWidth - POLE
        ? bokKray.right + ZAZOR
        : bokKray.left - ZAZOR - t.width)
      : seredina - t.width / 2;
    const predel = window.innerWidth - POLE - t.width;
    left = Math.min(Math.max(POLE, left), Math.max(POLE, predel));

    /*
     * ⛔ ХВОСТИК ПОКАЗЫВАЕТ НА СВОЙ ЭЛЕМЕНТ ДАЖЕ ПОСЛЕ ЗАВОРОТА. Подсказку у
     * края окна сдвигают внутрь — и без этого пересчёта хвостик остался бы по
     * центру подсказки, то есть указывал бы на соседа.
     */
    const hvost = Math.round(Math.min(Math.max(seredina - left, HVOST + 4), Math.max(HVOST + 4, t.width - HVOST - 4)));

    setPoza({ left: Math.round(left), top: Math.round(top), snizu, hvost, sboku });
  }, [cel]);

  if (cel === null) return null;

  return createPortal(
    <div
      ref={telo}
      /* ⛔ У БОКОВОЙ ПОЗЫ ХВОСТИКА НЕТ: он смотрел бы вверх, а значок сбоку. */
      className={`uz-sloy-podskazki${poza !== null && ! poza.snizu ? ' podskazka-sverhu' : ''}${poza !== null && poza.sboku ? ' podskazka-sboku' : ''}`}
      role="tooltip"
      style={poza === null
        ? { left: 0, top: 0, visibility: 'hidden' }
        : { left: poza.left, top: poza.top, '--hvost': `${poza.hvost}px` } as CSSProperties}
    >
      {cel.tekst}
    </div>,
    document.body,
  );
}
